import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Title3,
  Button,
  Spinner,
} from '@fluentui/react-components';
import { ArrowLeft20Regular } from '@fluentui/react-icons';
import { useProject } from '../../hooks/useProject';
import { Project, ProjectCreate } from '../../types';
import { ApiErrorInfo } from '../../types/api';
import ProjectForm from './components/ProjectForm';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  backButton: {
    minWidth: 'auto',
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
});


const ProjectFormPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  
  const [editingProject, setEditingProject] = React.useState<Project | undefined>();
  const [wasSaving, setWasSaving] = React.useState(false);
  const [currentError, setCurrentError] = React.useState<ApiErrorInfo | null>(null);
  const [isLoadingProject, setIsLoadingProject] = React.useState(!!projectId);
  const [projectNotFound, setProjectNotFound] = React.useState(false);

  const {
    projects,
    saveLoading,
    saveError,
    createProject,
    updateProject,
    fetchProjects,
  } = useProject();

  // Sincronizar erro atual com saveError
  React.useEffect(() => {
    if (saveError) {
      setCurrentError(saveError as ApiErrorInfo);
    }
  }, [saveError]);

  // Carregar lista de projetos
  React.useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Buscar projeto específico quando a lista for carregada
  React.useEffect(() => {
    if (projectId && projects && projects.length > 0) {
      const project = projects.find(p => p.id === Number(projectId));
      if (project) {
        setEditingProject(project);
        setProjectNotFound(false);
      } else {
        console.warn('Projeto não encontrado na lista:', projectId);
        setProjectNotFound(true);
      }
      setIsLoadingProject(false);
    } else if (!projectId) {
      setIsLoadingProject(false);
      setProjectNotFound(false);
    }
  }, [projectId, projects]);

  const handleSaveProject = async (projectData: ProjectCreate) => {
    setWasSaving(true);
    setCurrentError(null);
    try {
      if (editingProject) {
        await updateProject(editingProject.id, projectData);
      } else {
        await createProject(projectData);
      }
    } catch (error) {
      console.error('Erro ao salvar projeto:', error);
    }
  };

  // Navegar de volta quando salvar com sucesso
  React.useEffect(() => {
    if (wasSaving && !saveLoading) {
      if (!saveError) {
        navigate('/projects');
      } else {
        setWasSaving(false);
      }
    }
  }, [wasSaving, saveLoading, saveError, navigate]);

  const handleCancel = () => {
    navigate('/projects');
  };

  const handleBack = () => {
    navigate('/projects');
  };

  if (isLoadingProject) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner label="Carregando projeto..." size="large" />
        </div>
      </div>
    );
  }

  if (projectNotFound) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={handleBack}
            className={styles.backButton}
          />
          <Title3 className={styles.title}>Projeto não encontrado</Title3>
        </div>
        <div className={styles.content}>
          <p>O projeto solicitado não foi encontrado.</p>
          <Button onClick={handleBack}>Voltar para lista</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft20Regular />}
          onClick={handleBack}
          className={styles.backButton}
        />
        <Title3 className={styles.title}>
          {editingProject ? 'Editar Projeto' : 'Novo Projeto'}
        </Title3>
      </div>

      <div className={styles.content}>
        <ProjectForm
          project={editingProject}
          onSave={handleSaveProject}
          onCancel={handleCancel}
          loading={saveLoading}
          error={currentError}
        />
      </div>
    </div>
  );
};

export default ProjectFormPage;
